import moment from 'moment';
import { useContext } from 'react';
import * as TrackingService from 'common/services/tracking.service';
import { SHORT_DATE_FORMAT, TRACKING } from 'common/constants/constants';
import { getColor } from 'common/constants/_colors';
import MetricsInspectionContext from '../../../../state/context';
import {
  onScatterPointHover,
  onScatterPointClick,
  onSelectMultiplePoints
} from '../../../../state/actions';
import { yFormatter as getYFormatter } from '../../../../../CustomReport/Widgets/utils/formatters';
import { getAreaSelectionPointsFromEvents } from '../../../../../CustomReport/custom-report.util';
import { renderToString } from 'react-dom/server';
import Tooltip from '../Tooltip/Tooltip';
import useWorkingDaysSettings from 'views/root/views/home/hooks/useWorkingDaysSettings';
import usePeriods from '../../../../state/hooks/usePeriods';
import { getTimeRangesByGranularity } from 'views/root/views/home/views/dashboard/util';
import useSearchRanges from '../../../../../CustomReport/Widgets/CustomWidget/useSearchRanges';
import useFilters from '../../../AreaChart/useFilters';
import { getAreaPointRange, getTickPositions } from '../utils';

const DEFAULT_POINT_COLOR = getColor('astronaut');
const DEFAULT_POINT_COLOR_HOVER = getColor('indigoLight');
const ACTIVE_POINT_COLOR = getColor('melrose');
const ACTIVE_POINT_COLOR_HOVER = getColor('portage');

const generateItemId = item => `${moment(item.time).valueOf()}-${item.value}`;

const getEmptyStateData = (lastPeriod, granularity, workingDaysSetting) => {
  const rollupDateRanges = getTimeRangesByGranularity(
    new Date(lastPeriod.after),
    new Date(lastPeriod.before),
    granularity,
    true,
    workingDaysSetting
  );
  return rollupDateRanges.map((item, index) => ({
    x: moment(item.after).valueOf(),
    y: null
  }));
};

const useGraph = ({ data = [] }) => {
  const { dispatch, state: contextState } = useContext(
    MetricsInspectionContext
  );

  const { lastPeriod } = usePeriods();
  const filtersForGraph = useFilters();

  const { selectedPoints, areaHoveredPoint, widget, granularity, filters } =
    contextState;
  const searchRanges = useSearchRanges({
    filters: filtersForGraph,
    iterations: []
  });
  const plotBandsRange =
    searchRanges?.searchRange?.length > 1
      ? getAreaPointRange(areaHoveredPoint)
      : {};
  const searchRange = searchRanges?.searchRange || [];
  const tickPositions = getTickPositions(searchRange, data);
  const selectedPointsIds = selectedPoints.map(point => point.id);
  const yFormatter = getYFormatter(widget.unitType);
  const workingDaysSetting = useWorkingDaysSettings({
    teams: filters.team ? [filters.team] : []
  });

  const trackingPoints = event => {
    TrackingService.logEvent(event, {
      page: TRACKING.PAGES.METRICS_INSPECTION,
      widget_name: widget?.name
    });
  };

  const onAreaSelect = e => {
    e.preventDefault();
    const selectedPoints = getAreaSelectionPointsFromEvents(e);
    trackingPoints(TRACKING.EVENTS.GRAPH_AREA_SELECT);
    dispatch(onSelectMultiplePoints(selectedPoints));
  };
  const isEmptyState = data?.length === 0;
  const series = [
    {
      name: 'Events',
      id: 'events',
      color: DEFAULT_POINT_COLOR,
      data: isEmptyState
        ? getEmptyStateData(lastPeriod, granularity, workingDaysSetting)
        : data.map(item => {
            const pointId = generateItemId(item);
            const isPointSelected = selectedPointsIds.includes(pointId);

            return {
              id: pointId,
              y: item.value,
              x: moment(item.time).valueOf(),
              extraData: { ...item },
              color: isPointSelected ? ACTIVE_POINT_COLOR : DEFAULT_POINT_COLOR,
              marker: {
                states: {
                  hover: {
                    fillColor: isPointSelected
                      ? ACTIVE_POINT_COLOR_HOVER
                      : DEFAULT_POINT_COLOR_HOVER
                  }
                }
              }
            };
          })
    }
  ];

  const graphConfig = {
    chart: {
      type: 'scatter',
      zoomType: 'xy',
      selectionMarkerFill: getColor('pigeonPost', 0.25),
      events: {
        selection: onAreaSelect
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      type: 'datetime',
      ...(tickPositions.length && { tickPositions }),
      title: {
        enabled: false
      },
      labels: {
        formatter() {
          return moment.unix(this.value / 1000).format(SHORT_DATE_FORMAT);
        }
      },
      plotBands: [plotBandsRange]
    },
    yAxis: {
      title: {
        enabled: false
      },
      tickAmount: 6,
      ...(isEmptyState ? { min: 0, max: 5 } : {}), //we need this in order to draw the y-axis lines in case of no data
      labels: {
        x: -25,
        formatter() {
          return yFormatter(this.value);
        }
      }
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      series: {
        stickyTracking: false,
        cursor: 'pointer',
        point: {
          events: {
            click() {
              trackingPoints(TRACKING.EVENTS.GRAPH_POINT_CLICK);
              dispatch(onScatterPointClick(this));
            },
            mouseOut() {
              dispatch(onScatterPointHover(null));
            },
            mouseOver() {
              dispatch(onScatterPointHover(this));
            }
          }
        }
      },
      scatter: {
        marker: {
          radius: 5,
          symbol: 'circle',
          lineWidth: 1,
          states: {
            hover: {
              enabled: true,
              lineWidth: 2
            }
          }
        }
      }
    },
    tooltip: {
      useHTML: true,
      backgroundColor: '#FFF',
      stickOnContact: false,
      stickyTracking: false,
      hideDelay: 0,
      borderWidth: 0,
      shadow: false,
      padding: 0,
      formatter() {
        return renderToString(
          <Tooltip event={this} contextState={contextState} />
        );
      }
    },
    series,
    credits: {
      enabled: false
    }
  };

  return graphConfig;
};

export default useGraph;
